/**
 * useSceneThumbs - each wings card's set, for its wireframe thumbnail (plan map-display, I).
 *
 * The scene on stage is the live set (the panel's own solids, already meshed). A scene in the wings is read
 * from its own terrain documents (`read`) the first time its card is shown, and again whenever it is parked
 * anew (`lastOnStageAt` changes): a read that found nothing is tried again then too. A read that fails is
 * logged and the card simply has no thumbnail. Outside a session there is no `read` and no wings to show.
 *
 * @param {{ scenes: object[], onStageId: string|null, stageSolids: object[]|null,
 *   read: ((sceneId: string) => Promise<{ terrain: object|null }>)|null }} args
 * @returns {(sceneId: string) => {solids: object[], extent?: object}|null} the scene's set, or null while unknown
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useMirror from './useMirror';
import { boundaryExtent } from '../engine/battlefield/boundary';
import { terrainSolids } from '../engine/battlefield/presets';
import { unpackTerrain } from '../engine/battlefield/mapFile';
import { diag, errorSummary } from '../diagnostics/log';

/** Which parking of a scene its set was read for. */
const versionOf = (scene) => `${scene.id}:${scene.lastOnStageAt ?? ''}`;

/** A set as the thumbnail draws it: its solids, and the map's own bounds to fit them in. */
const setOf = (solids, boundary) => ({ solids, extent: boundary ? boundaryExtent(boundary) : undefined });

/** One scene's set, read and handed on (null when it has none yet); a failed read is logged. */
function readSet(read, sceneId, onSet) {
  read(sceneId)
    .then(({ terrain }) => {
      const t = terrain && unpackTerrain(terrain);
      onSet(t ? setOf(terrainSolids(t), t.boundary) : null);
    })
    .catch((err) => diag.warn('scenes', 'thumbnail terrain read failed', errorSummary(err)));
}

export default function useSceneThumbs({ scenes, onStageId, stageSolids, stageBoundary, read }) {
  const [parked, setParked] = useState({});
  const asked = useRef(new Set());
  const mounted = useRef(false);
  const reader = useMirror(read);
  const canRead = !!read;
  useEffect(() => {
    // Set here, not at creation: StrictMode's rehearsal unmounts once, and a flag only cleared would stay clear.
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!reader.current) return;
    const unread = (scenes ?? []).filter((s) => s.id !== onStageId && !asked.current.has(versionOf(s)));
    for (const scene of unread) {
      const version = versionOf(scene);
      asked.current.add(version);
      readSet(reader.current, scene.id, (set) => {
        if (!set) asked.current.delete(version);
        else if (mounted.current) setParked((was) => ({ ...was, [scene.id]: set }));
      });
    }
    // `canRead`, not the reader: the shell hands a new one each render, and only its presence matters here.
  }, [scenes, onStageId, canRead, reader]);
  const onStage = useMemo(() => setOf(stageSolids ?? [], stageBoundary), [stageSolids, stageBoundary]);
  return useCallback((id) => (id === onStageId ? onStage : (parked[id] ?? null)), [onStageId, onStage, parked]);
}
